import React, { useEffect, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import useStore from '@store';
import { listAPI } from '@api/client';
import { debounce } from '@utils/helpers';
import { Check, Edit3, Eye, Lock } from 'lucide-react';
import toast from 'react-hot-toast';

export default function Editor() {
  const { 
    content, 
    setContent,
    isProtected, 
    isOwner, 
    isLoading,
    isSaving,
    setSaving,
    lastSaved,
    collaborators,
    listId,
    password,
    isConnected,
    tier,        // Add this
    hasPassword, // Add this 
    expiresAt    // Add this  
  } = useStore();
  
  const isReadOnly = hasPassword && !isOwner;
  
  // Auto-save function
  const saveContent = useCallback(
    debounce(async (content) => {
      if (!listId || (isProtected && !password)) return;
      
      setSaving(true);
      try {
        await listAPI.updateContent(listId, content);
        useStore.setState({ lastSaved: new Date() });
      } catch (error) {
        console.error('Failed to save:', error);
        toast.error('Failed to save changes');
      } finally {
        setSaving(false);
      }
    }, 500),
    [listId, isProtected, password, setSaving]
  );
  
  // Welcome content that matches the mockup exactly
  
  
  // Initialize editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      TaskList.configure({
        HTMLAttributes: {
          class: 'task-list',
        },
      }),
      TaskItem.configure({
        nested: true,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 hover:underline cursor-pointer',
        },
      }),
      Placeholder.configure({
        placeholder: isReadOnly ? '' : `Start typing to create your list...
      
      ### Headings
      Use ### for large headings
      
      **Bold text** and *italic text*
      
      - Bullet points
      - Another bullet point
      
      1. Numbered lists
      2. Work great too
      
      [ ] Checkboxes for tasks
      [x] Check them off when done
      
      Links work automatically: https://example.com
      
      ---
      
      Press Share to get a link others can view and edit
      Press Lock to password-protect for $1.99/month`,
      }),
    ],
    content: content || '',
    editable: !isReadOnly,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setContent(html);
      saveContent(html);
    },
  });
  
  // WebSocket sync effect
  useEffect(() => {
    if (editor && content && content !== editor.getHTML()) {
      console.log('Updating editor with:', content);
      console.log('Current editor content:', editor.getHTML());
      
      editor.commands.setContent(content, false);
      editor.view.dispatch(editor.view.state.tr);
    }
  }, [content, editor]);
  
  // Add this useEffect right after the editor initialization
  useEffect(() => {
    if (editor) {
      editor.setEditable(!isReadOnly);
    }
  }, [editor, isReadOnly]);
  // Auto-reload on interaction when disconnected
  useEffect(() => {
    if (!editor) return;
    
    const handleEditorFocus = () => {
      setTimeout(() => {
        if (!isConnected) {
          console.log('Editor focused but WebSocket disconnected, reloading...');
          window.location.reload();
        }
      }, 100);
    };
    
    const handleEditorClick = () => {
      setTimeout(() => {
        if (!isConnected) {
          console.log('Editor clicked but WebSocket disconnected, reloading...');
          window.location.reload();
        }
      }, 1000);
    };
    
    editor.view.dom.addEventListener('focus', handleEditorFocus);
    editor.view.dom.addEventListener('click', handleEditorClick);
    
    return () => {
      editor.view.dom.removeEventListener('focus', handleEditorFocus);
      editor.view.dom.removeEventListener('click', handleEditorClick);
    };
  }, [editor, isConnected]);
  
  // Set initial content when editor is ready
  useEffect(() => {
    if (editor && content && editor.isEmpty) {
      editor.commands.setContent(content, false);
    }
  }, [editor, content]);
  
  // Calculate editors vs viewers (placeholder logic)
  const editors = collaborators > 0 ? 1 : 0;
  const viewers = Math.max(0, collaborators - 1);
  
  // Format last saved time
  const formatLastSaved = (date) => {
    if (!date) return 'Just now';
    const now = new Date();
    const diff = now - new Date(date);
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  };

  // Add this function inside Editor component
// Add this function inside the Editor component
const formatExpiration = (expiresAt) => {
  if (!expiresAt) return '';
  
  const now = new Date();
  const expires = new Date(expiresAt);
  const diff = expires - now;
  
  if (diff <= 0) return 'Expired';
  
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  const hours = Math.floor((diff % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  const minutes = Math.floor((diff % (60 * 60 * 1000)) / (60 * 1000));
  
  if (days > 1) return `Expires in ${days} days`;
  if (days === 1) return `Expires tomorrow`;
  if (hours > 1) return `Expires in ${hours} hours`;
  if (hours === 1) return `Expires in 1 hour`;
  if (minutes > 30) return `Expires in ${minutes} minutes`;
  return 'Expires very soon';
};
  
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        backgroundColor: '#f8fafc',
        color: '#64748b'
      }}>
        Loading...
      </div>
    );
  }
  
  return (
    <div style={{ 
      height: '100vh', 
      backgroundColor: '#f8fafc',
      paddingTop: '64px', // Account for fixed header
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Status Bar */}
      <div style={{
        background: '#f1f5f9',
        borderBottom: '1px solid #e2e8f0',
        padding: '12px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#059669',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          {isSaving ? (
            <>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #e2e8f0',
                borderTop: '2px solid #3b82f6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <span style={{ color: '#64748b' }}>Saving...</span>
            </>
          ) : (
            <>
              <Check size={16} style={{ color: '#059669' }} />
              <span>Auto-saved</span>
            </>
          )}
        </div>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          color: '#64748b',
          fontSize: '14px'
        }}>
          {editors > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <Edit3 size={16} />
              <span>{editors}</span>
            </div>
          )}
          {viewers > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <Eye size={16} />
              <span>{viewers}</span>
            </div>
          )}
        </div>
      </div>
      {/* {isReadOnly && (
        <div style={{
          backgroundColor: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '8px',
          padding: '12px',
          margin: '0 24px 16px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px',
          color: '#92400e'
        }}>
          <Lock size={16} />
          <span>This list is password protected. You can view but not edit.</span>
        </div>
      )} */}
      {/* Main Content */}
      <div style={{
        maxWidth: '800px',
        width: '100%',
        margin: '24px auto',
        padding: '0 24px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            padding: '32px',
            flex: 1,
            outline: 'none',
            overflow: 'auto'
          }}>
            <EditorContent 
              editor={editor}
              style={{
                outline: 'none',
                fontSize: '16px',
                lineHeight: '1.7'
              }}
            />
          </div>
        </div>
        
        {/* Footer Status */}
        <div style={{
          marginTop: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: '#64748b',
          fontSize: '14px'
        }}>
          <div>
            {formatLastSaved(lastSaved)}
            {tier === 'free' && expiresAt && (
              <span style={{ color: '#ef4444', marginLeft: '8px' }}>
                • {formatExpiration(expiresAt)}
              </span>
            )}
          </div>
          <div>
            {tier === 'paid' && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#10b981'
                }}>
                  <span>✓</span>
                  <span>Permanent</span>
                </div>
                {hasPassword && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#059669'
                  }}>
                    <Lock size={16} />
                    <span>Protected</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}